import { isValidBIC, isValidIBAN } from "ibantools"

export const WHITESPACES_REGEX = /\s/g
export const ALL_BUT_DIGITS_REGEX = /\D/g

export function isEmptyOrBlank(
  input: string | boolean | number | Big | undefined | null,
): boolean {
  return input == undefined || input.toString().trim().length == 0
}

const integerOnlyRegex = /^\d+$/

export function isIntegerNumberValid(input?: number | string): boolean {
  return input != undefined
    ? input.toString().match(integerOnlyRegex) != null
    : false
}

const dateTimeRegex =
  /^(?:(?:(?:(?:(?:(?:0[1-9]|[1-2][0-9]|3[01])\.(?:0[13578]|1[02]))|(?:(?:0[1-9]|[1-2][0-9]|30)\.(?:0[469]|11))|(?:(?:0[1-9]|1[0-9]|2[0-8])\.02))\.\d{4})|(?:29\.02\.(?:(?:\d{2}(?:0[48]|[2468][048]|[13579][26]))|(?:(?:[02468][048])|[13579][26])00))) (0[0-9]|1[0-9]|2[0-3]):([0-9]|[0-5][0-9]))$/
const dateRegex =
  /^(?:(?:(?:(?:0[1-9]|[1-2][0-9]|3[01])\.(?:0[13578]|1[02]))|(?:(?:0[1-9]|[1-2][0-9]|30)\.(?:0[469]|11))|(?:(?:0[1-9]|1[0-9]|2[0-8])\.02))\.\d{4})|(?:29\.02\.(?:(?:\d{2}(?:0[48]|[2468][048]|[13579][26]))|(?:(?:[02468][048])|[13579][26])00))$/
const timeRegex = /^(([0-1][0-9])|(2[0-3])):[0-5][0-9]$/

export function isDateTimeValid(dateTime: string): boolean {
  return dateTime.match(dateTimeRegex) != null
}
export function isDateValid(date: string): boolean {
  return date.match(dateRegex) != null
}
export function isTimeValid(time: string): boolean {
  return time.match(timeRegex) != null
}

// Adapted and slightly customized based on:
// https://github.com/manishsaraan/email-validator/blob/master/index.js
const emailRegex =
  /^[-!#$%&'*+äöüÄÖÜ/0-9=?^_a-z`{|}~](\.?[-!#$%&'*+äöüÄÖÜ/0-9=?^_a-z`{|}~])*@[a-z0-9](-*\.?[a-z0-9])*\.[a-z](-?[a-z0-9])+$/

export function isEmailValid(email?: string): boolean {
  if (email == undefined) return false

  const sanitizedEmail = toWithoutSpacesLower(email)
  const emailParts = sanitizedEmail.split("@")
  if (emailParts.length != 2) return false

  const [userPart, domainPart] = emailParts
  if (
    userPart.length == 0 ||
    domainPart.length == 0 ||
    userPart.length > 64 ||
    domainPart.length > 255
  )
    return false

  const domainParts = domainPart.split(".")
  if (
    domainParts.length < 2 ||
    domainParts.some(({ length }) => length == 0 || length > 63)
  )
    return false

  return sanitizedEmail.match(emailRegex) != null
}

const phoneNumberPlusRegex = /\+/g
const phoneNumberSlashRegex = /\//g
const phoneNumberCleanerRegex = /[\s-+/]/g
const phoneNumberRegex = /^\d{8,15}$/

export function isPhoneNumberValid(phoneNumber?: string): boolean {
  if (phoneNumber == undefined) return false

  const plusCount = phoneNumber.match(phoneNumberPlusRegex)?.length ?? 0
  if (plusCount > 0 && (plusCount != 1 || !phoneNumber.startsWith("+")))
    return false

  if ((phoneNumber.match(phoneNumberSlashRegex)?.length ?? 0) > 1) return false
  if (phoneNumber.includes("--")) return false

  return (
    phoneNumber.replace(phoneNumberCleanerRegex, "").match(phoneNumberRegex) !=
    null
  )
}

const onlyDigitsRegex = /[0-9]/
const onlyLowercaseRegex = /[a-z]/
const onlyUppercaseRegex = /[A-Z]/
const onlySpecialCharsRegex = /[^a-zA-Z0-9 ]/

export function isStrongPasswordValid(password?: string): boolean {
  if (password == undefined || password.length < 15) return false

  if (password.length < 25) {
    if (password.match(onlyDigitsRegex) == null) return false
    if (password.match(onlyLowercaseRegex) == null) return false
    if (password.match(onlyUppercaseRegex) == null) return false
    return password.match(onlySpecialCharsRegex) != null
  }

  return !isEmptyOrBlank(password)
}

export function isRepeatedPasswordValid(
  repeatedPassword?: string,
  password?: string,
): boolean {
  return !isEmptyOrBlank(repeatedPassword) && repeatedPassword == password
}

const uidRegex = /^ATU\d{8}$/
const taxNumberRegex = /^\d{2}-\d{3}\/\d{4}$/

export function isTaxIdValid(taxId?: string): boolean {
  if (taxId == undefined) return false

  const sanitizedTaxId = toWithoutSpacesUpper(taxId)

  return (
    sanitizedTaxId.match(uidRegex) != null ||
    sanitizedTaxId.match(taxNumberRegex) != null
  )
}

export function isIBANValid(iban?: string): boolean {
  return iban != undefined ? isValidIBAN(toWithoutSpacesUpper(iban)) : false
}

export function isBICValid(bic?: string): boolean {
  return bic != undefined ? isValidBIC(toWithoutSpacesUpper(bic)) : false
}

// Utility functions

function toWithoutSpaces(input: string) {
  return input.replace(WHITESPACES_REGEX, "")
}

function toWithoutSpacesLower(input: string) {
  return toWithoutSpaces(input).toLowerCase()
}

function toWithoutSpacesUpper(input: string) {
  return toWithoutSpaces(input).toUpperCase()
}
